import {datasourcestore, List, ListEventMap, Store} from "@intermesh/goui";
import {AclLevel} from "@intermesh/groupoffice-core";
import {Board, Card, cardDS, Column} from "./Index.js";
import {cardComp} from "./CardComponent.js";
import {getCardEntityData} from "./BacklogColumn.js";

export interface CardGridEventMap extends ListEventMap {
	cardcreatedelete: { boardId: string }
}

export class CardGrid extends List<Store<Card>, CardGridEventMap> {

	constructor(public column: Column, private board: Board) {
		const cardStore = datasourcestore({
			dataSource: cardDS,
			onBeforeLoad: async cards => {
				const entityDataPromises = cards.map(card => getCardEntityData(card));
				const entityDataResults = await Promise.all(entityDataPromises);

				return cards.map((card, index) => {
						if (entityDataResults[index].entityData) {
							return Object.assign(
								{
									entityData: entityDataResults[index].entityData,
									responsibleUserIds: entityDataResults[index].responsibleUserIds
								},
								card
							)
						} else {
							return card
						}
					}
				);
			},
			sort: [{property: "sortOrder", isAscending: true}]
		});

		const renderer = (record: any) => {
			if (record.entityData) {
				return [cardComp({record: record})];
			} else {
				return ""
			}
		}

		super(cardStore, renderer);

		this.column = column;

		cardStore.setFilter("column", {
			columnId: this.column.id
		});

		cardStore.setFilter("board", {
			boardId: this.board.id
		});

		void cardStore.load();

		this.stateId = "kanban-column-" + this.column.id;
		this.cls = "scroll kanban-card-grid";
		this.flex = 1;
		this.sortableGroup = "kanban-card";
		this.emptyStateHtml = "";
		this.dropOn = false;
		this.dropBetween = true;
		this.draggableRows = this.board.permissionLevel >= AclLevel.WRITE || go.Modules.get("business", "kanban").userRights.mayManageBoards;

		this.rowSelectionConfig = {
			multiSelect: false
		};

		this.on("rowdblclick", async ({storeIndex}) => {
			const record = this.store.get(storeIndex)!;

			const win = new go.links.LinkDetailWindow({
				entity: record.entity
			});

			win.load(record.entityId);
		});

		this.on("drop", async ({source, target, dragDataSet, fromIndex, toIndex}) => {
			const draggedCard: Card = dragDataSet.selectedRowIndexes[0].record;
			const toCol = target.column as Column;
			const fromCol = (source as CardGrid).column;

			if (!draggedCard || !toCol) {
				return;
			}

			const promises: Promise<unknown>[] = [];
			let isNewCard = false;

			if (!draggedCard.id && !fromCol) { // card from backlog
				isNewCard = true;
				const toCards = this.store.data.slice();
				toCards.splice(toIndex, 0, draggedCard);

				promises.push(
					cardDS.create({
						boardId: this.board.id,
						columnId: toCol.id,
						sortOrder: toIndex,
						entityId: draggedCard.entityId,
						entity: draggedCard.entity
					}),
					...this.buildCardUpdates(toCards, undefined, toIndex)
				);
			} else if (toCol.id === fromCol.id) { // move within column
				const cards = this.store.data.slice();
				cards.splice(fromIndex, 1);

				const insertIndex = fromIndex < toIndex ? toIndex - 1 : toIndex;
				cards.splice(insertIndex, 0, draggedCard);

				const start = Math.min(fromIndex, insertIndex);
				const end = Math.max(fromIndex, insertIndex);
				promises.push(...this.buildCardUpdates(cards, undefined, start, end));
			} else { // move to other column
				const toCards = target.store.data.slice();
				toCards.splice(toIndex, 0, draggedCard);
				promises.push(...this.buildCardUpdates(toCards, toCol.id, 0));
			}

			await Promise.all(promises);

			if (isNewCard) {
				this.fire("cardcreatedelete", {boardId: this.board.id});
			}
		});
	}


	private buildCardUpdates(cards: Card[], columnId?: string, start = 0, end = cards.length - 1): Promise<unknown>[] {
		const updates: Promise<unknown>[] = [];

		for (let i = start; i <= end; i++) {
			const card = cards[i];

			if (!card.id) {
				continue;
			}

			const columnChanged = columnId !== undefined && card.columnId !== columnId;

			if (card.sortOrder !== i || columnChanged) {
				updates.push(cardDS.update(card.id, {
					sortOrder: i,
					...(columnChanged ? {columnId} : {})
				}));
			}
		}

		return updates;
	}
}
